import { DataModel, DealEmployeeRoleTypes, FIProductType } from '@core/classes/SharedEnums';
import { RouteLocationNormalizedLoaded, Router } from 'vue-router';
import * as customValidators from '@core/services/custom-validators';
import * as defaultValidators from '@vuelidate/validators';
import $modal from '@core/services/modal';
import { AxiosResponse } from 'axios';
import { ComponentPublicInstance } from 'vue';
import { VINInfo } from '@core/classes/VINInfo';

import api from './api/_api';
import ENUMS from '@core/classes/Enums';
import FIMenu from '@core/classes/FIMenu';
import FIMenuBuyersOrderTaxGroup from '@core/classes/FIMenuBuyersOrderTaxGroup';
import FIMenuDataModelBase from '@core/classes/_Base/FIMenuDataModelBase';
import LogRocket from 'logrocket';
import mitt from 'mitt';
import modalCopyDeal from '@core/modals/modalCopyDeal.vue';
import modalInfo from '@core/modals/modalInfo.vue';
import modalInputs from '@core/modals/modalInputs.vue';
import modalLockedMessage from '@core/modals/modalLockedMessage.vue';
import moment from 'moment';
import PartnerCode from '@core/classes/Store/PartnerCode';
import settings from 'settings';
import Store from '@core/classes/Store/Store';

//FIXME: Delete this later
export const EventBusCore = mitt();
/**
 * The type of the date time format style.
 */
type DateTimeFormatStyle = 'short' | 'full' | 'long' | 'medium' | undefined;
/**
 * The type of the generic function.
 */
type GenericFunction = (value?: any) => any;

export type SaturnDate = string | Date | moment.Moment;

/**
 * The util object.
 */
const util = {
	/**
	 * Clones an object.
	 * @param {T} obj - The object to clone.
	 * @returns {T} The cloned object.
	 */
	clone<T>(obj: T) {
		return JSON.parse(JSON.stringify(obj)) as T;
		//TODO: Research if this always produces the same output as JSON.parse(JSON.stringify(obj))
		// return structuredClone(obj) as T;
	},

	/**
	 * This method updates object 1's properties with object 2's and add them if necessary.
	 * both object most be of the same data type
	 * @param {Object} obj1
	 * @param {Object} obj2
	 */
	deepUpdate(obj1: any, obj2: any) {
		// Iterate over all properties in obj2
		for (const key in obj2) {
			// Check if obj2 has this property itself (not inherited)
			if (obj2.hasOwnProperty(key)) {
				// Check if both obj1[key] and obj2[key] are arrays
				if (Array.isArray(obj2[key]) && Array.isArray(obj1[key])) {
					// Iterate over the elements of obj2's array
					for (let i = 0; i < obj2[key].length; i++) {
						// If the element in obj2 is an object, and obj1 has a corresponding element
						if (typeof obj2[key][i] === 'object' && obj2[key][i] !== null) {
							// Recursively update the object in the array, if it exists in obj1
							if (obj1[key][i]) {
								util.deepUpdate(obj1[key][i], obj2[key][i]);
							} else {
								// If the element doesn't exist in obj1's array, simply add it
								obj1[key][i] = obj2[key][i];
							}
						} else {
							// For primitive elements, replace the element in obj1's array with obj2's
							obj1[key][i] = obj2[key][i];
						}
					}

					// Check if the property is an object (but not null)
				} else if (typeof obj2[key] === 'object' && obj2[key] !== null) {
					// If obj1 also has this property as an object, recursively update it
					if (typeof obj1[key] === 'object' && obj1[key] !== null) {
						util.deepUpdate(obj1[key], obj2[key]);

						// Otherwise, replace the property in obj1 with the one from obj2
					} else {
						obj1[key] = obj2[key];
					}

					// If the property is a primitive (number, string, boolean, etc.)
				} else {
					// Directly assign the value from obj2 to obj1
					obj1[key] = obj2[key];
				}
			}
		}
	},

	/**
	 * Checks if a value is a function.
	 * @param {unknown} functionToCheck - The value to check.
	 * @returns {boolean} True if the value is a function, false otherwise.
	 */
	isFunction(functionToCheck: unknown) {
		return typeof functionToCheck === 'function';
	},

	/**
	 * Checks if a value is null.
	 * @param {unknown} val - The value to check.
	 * @returns {boolean} True if the value is null, false otherwise.
	 */
	isNull(val: unknown) {
		return val == null || typeof val === 'undefined';
	},

	/**
	 * Checks if a value is null or empty.
	 * @param {unknown} value - The value to check.
	 * @param {boolean} trim - Whether to trim the value.
	 * @returns {boolean} True if the value is null or empty, false otherwise.
	 */
	isNullOrEmpty(value: unknown, trim = false) {
		return value == null || (trim && typeof value === 'string' ? value.trim() : value) == '';
	},

	/**
	 * Checks if a value has a value.
	 * @param {unknown} val - The value to check.
	 * @returns {boolean} True if the value has a value, false otherwise.
	 */
	hasValue(val: unknown) {
		return !(val == null || typeof val === 'undefined');
	},

	/**
	 * Checks if any of the arguments are null.
	 * @param {...unknown[]} args - The values to check.
	 * @returns {boolean} True if any of the values are null, false otherwise.
	 */
	anyNull(...args: unknown[]) {
		return args.some(val => util.isNull(val));
	},

	/**
	 * Does a shallow compare of two lists and returns true if any value appears in both lists.
	 * @param {Array} array1
	 * @param {Array} array2
	 */
	doListsIntersect<T1, T2>(array1: T1[], array2: T2[]) {
		if (!Array.isArray(array1) || !Array.isArray(array2)) throw new Error('All arguments must be arrays. Cannot process.');

		const shortestList = array1.length < array2.length ? array1 : array2;
		const longestList = array1.length < array2.length ? array2 : array1;

		return shortestList.some(itemA => longestList.some(itemB => itemB === itemA));
	},

	/**
	 * Checks if a given object has a key. Returns false if the property is currently `undefined`.
	 * @param {object} obj The object to check
	 * @param {string} name The name of the object key to validate
	 * @returns {boolean} True if the object has the key, false otherwise.
	 */
	hasKey(obj: Record<string, unknown>, key: string) {
		return key in obj && obj[key] !== undefined;
	},

	/**
	 * Displays the number of months or years in a given number of months.
	 * @param {number} months - The number of months to display.
	 * @returns {string} The number of months or years in a given number of months.
	 */
	displayMonthsYears(months: number) {
		if (months && typeof months == 'number') {
			//mod 12 so it is a year
			if (months % 12 === 0) return (months / 12).toString() + ' Years';
			else return months.toString() + ' Months';
		} else return months;
	},

	/**
	 * Formats the toastr text.
	 * @param {string | number | { status: keyof typeof ENUMS.API_ERROR_DESCRIPTION; message: string }} error - The error to format.
	 * @returns {string} The formatted toastr text.
	 */
	formatToastrText(error: string | number | { status: keyof typeof ENUMS.API_ERROR_DESCRIPTION; message: string }) {
		if (typeof error == 'string') {
			return error;
		} else if (typeof error == 'number') {
			return error.toString();
		}
		try {
			return `${error.status}: ${ENUMS.API_ERROR_DESCRIPTION[error.status].status}</br> ${ENUMS.API_ERROR_DESCRIPTION[error.status].message}`;
		} catch (e: unknown) {
			return `Unknown error. ${e}`;
		}
	},

	/**
	 * Displays a toastr notification.
	 * @param {string} type - The type of toastr notification to display.
	 * @param {string} title - The title of the toastr notification.
	 * @param {unknown} text - The text of the toastr notification.
	 * @param {number} duration - The duration of the toastr notification.
	 */
	toastr(type: string, title: string, text: unknown, duration = 3000) {
		//READ THE DOCS PLZ: https://www.npmjs.com/package/vue-notification
		//if inside a js file use Vue.notify - see request.js for an example

		if (Array.isArray(text)) {
			text = text.join('</br></br> ');
		}

		const notifData = {
			type,
			title,
			text,
			duration,
		};

		// console.log("Toast Stuff", type, title, text)
		EventBusCore.emit('toastr', notifData);

		//notify({
		//group: 'saturn',
		//title: title,
		//type: type,
		//duration: 500,
		//text: this.formatToastrText(text),
		//closeOnClick: true
		//});
	},

	/**
	 * Plays a sound file.
	 * @param {string} path - The path to the sound file.
	 * @param {number} volumeLevel - The volume level of the sound file.
	 * @returns {Audio} The audio object.
	 */
	playSound(path: string, volumeLevel: number) {
		const audio = new Audio(path);
		/* eslint-disable no-console */
		console.log('playing audio - volumeLevel', volumeLevel);
		audio.volume = volumeLevel;
		const playPromise = audio.play();

		if (playPromise !== undefined) {
			playPromise
				.then(_ => {
					//console.log('Did you hear that?');
				})
				.catch(error => {
					console.log(`playSound error: ${error}`);
				});
		}

		return audio;
	},

	/**
	 * Formats a dollar amount string to a number.
	 * @param {string} dollarAmountString - The dollar amount string to format.
	 * @returns {number} The formatted number.
	 */
	formatDollarAmountStringToNumber(dollarAmountString: string = '$0.00') {
		// Remove special characters, only keep digits and decimal points
		const cleanedString = dollarAmountString.replace(/[^\d.]/g, '');

		// Convert the cleaned string to a number
		return parseFloat(cleanedString);
	},

	/**
	 * Formats a price string to a number.
	 * @param {string | number} value - The price string to format.
	 * @param {number} toFixed - The number of decimal places to format to.
	 * @returns {string} The formatted price string.
	 */
	formatPrice(value: string | number, toFixed = 2) {
		if (value != null && typeof value !== 'undefined' && !Number.isNaN(parseFloat(value.toString()))) {
			return (
				'$' +
				parseFloat(value.toString())
					.toFixed(toFixed)
					.replace(/(\d)(?=(\d{3})+(?:\.\d+)?$)/g, '$1,')
					.toString()
			);
		} else {
			return '$' + parseFloat('0').toFixed(toFixed);
		}
	},

	/**
	 * Formats a number to a currency string.
	 * @param {number} value - The number to format.
	 * @param {string} isoCurrencyCode - The ISO currency code to format to.
	 * @returns {string} The formatted currency string.
	 */
	formatCurrency(value: number, isoCurrencyCode = 'USD') {
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: isoCurrencyCode,
		}).format(value ?? 0);
	},

	/**
	 * Formats a number to a US number string.
	 * @param {string | number} value - The number to format.
	 * @param {number} toFixed - The number of decimal places to format to.
	 * @returns {string} The formatted number string.
	 */
	formatUSNumbers(value: string | number, toFixed = 0) {
		if (value != null && typeof value !== 'undefined' && !Number.isNaN(parseFloat(value.toString()))) {
			return parseFloat(value.toString())
				.toFixed(toFixed)
				.replace(/(\d)(?=(\d{3})+(?:\.\d+)?$)/g, '$1,')
				.toString();
		} else {
			return parseFloat('0').toFixed(toFixed);
		}
	},

	/**
	 * Formats a phone number to a US phone number string.
	 * @param {string} number - The phone number to format.
	 * @returns {string} The formatted phone number string.
	 */
	formatPhoneNumber(number: string) {
		const includesCountryCode = number.length > 10;
		let formattedNumber = includesCountryCode
			? number.replace(/(\d{1,2})*(\d{3})(\d{3})(\d{4})/, '$1($2) $3-$4')
			: number.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');

		if (includesCountryCode && formattedNumber[0] !== '+') formattedNumber = `+${formattedNumber}`;

		return formattedNumber;
	},

	/**
	 * Formats a date to a date string.
	 * @param {SaturnDate} dateStr - The date to format.
	 * @param {DateTimeFormatStyle} dateStyle - The date style to format to.
	 * @param {DateTimeFormatStyle} timeStyle - The time style to format to.
	 * @returns {string} The formatted date string.
	 */
	formatDate(dateStr: SaturnDate, dateStyle: DateTimeFormatStyle = 'short', timeStyle: DateTimeFormatStyle = undefined) {
		const dateObj = dateStr instanceof Date ? dateStr : moment(dateStr).toDate();
		if (dateStyle === undefined && timeStyle === undefined) throw new Error('Cannot format if neither date nor time style are specified.');

		if (dateStyle === undefined && timeStyle !== undefined) return dateObj.toLocaleTimeString('en-US', { timeStyle });
		else if (timeStyle === undefined && dateStyle !== undefined) return dateObj.toLocaleDateString('en-US', { dateStyle });

		const dateTimeStyle = { dateStyle: dateStyle, timeStyle: timeStyle };
		return dateObj.toLocaleString('en-US', dateTimeStyle);
	},

	formatDateWithOptions(dateStr: SaturnDate, formatOptions: Intl.DateTimeFormatOptions): string {
		const dateObj = dateStr instanceof Date ? dateStr : moment(dateStr).toDate();
		return dateObj.toLocaleString('en-US', formatOptions);
	},

	/**
	 * Formats a date to a date string.
	 * @param {SaturnDate} dateOnlyStr - The date to format.
	 * @param {DateTimeFormatStyle} dateStyle - The date style to format to.
	 * @returns {string} The formatted date string.
	 */
	formatDateOnly(dateOnlyStr: SaturnDate, dateStyle: DateTimeFormatStyle = 'short') {
		const dateObj = dateOnlyStr instanceof Date ? dateOnlyStr : new Date(Date.parse(`${dateOnlyStr}T00:00:00`));
		return this.formatDate(dateObj, dateStyle, undefined);
	},

	/**
	 * Sets an input glow.
	 * @param {NodeListOf<Element>} glowInputs - The inputs to set the glow on.
	 * @param {string} glowClass - The class to add to the inputs.
	 * @param {number} glowTime - The time to remove the class from the inputs.
	 */
	setInputGlow(glowInputs: NodeListOf<Element>, glowClass: string, glowTime: number = 3700) {
		glowInputs.forEach(el => {
			el.classList.add(glowClass);
			setTimeout(() => {
				el.classList.remove(glowClass);
			}, glowTime);
		});
	},

	/**
	 * Gets an object key from a value.
	 * @param {T1} obj - The object to search.
	 * @param {T2} value - The value to search for.
	 * @returns {string | null} The key of the value.
	 */
	getObjectKeyFromValue<T1, T2>(obj: T1, value: T2) {
		const searchKey = Object.entries(obj).find(o => o[1] == value);
		return searchKey ? searchKey[0] : null;
	},

	/**
	 * Gets the default product lender mappings.
	 * @param {FIProductType} productType - The product type to search for.
	 * @param {string} provider - The provider to search for.
	 * @param {Store} store - The store to search for.
	 * @returns {any[] | null} The default product lender mappings.
	 */
	getDefaultProductLenderMappings(productType: FIProductType, provider: string, store: Store) {
		//console.log('getDefaultProductLenderMappings store', store);
		let defaultPlm = null;

		if (store.storeSettings.productLenderMappings.filter(plm => plm.productType == productType && plm.providerCode == provider).length > 0) {
			const temp = store.storeSettings.productLenderMappings.find(plm => plm.productType == productType && plm.providerCode == provider).lenderMappings;
			defaultPlm = temp.filter(plm => plm.lenderCodes.length == 0)[0].planMappings;
			if (defaultPlm.length == 0) {
				defaultPlm = null;
			}
		}

		return defaultPlm;
	},

	/**
	 * Compares two strings.
	 * @param {string} a - The first string to compare.
	 * @param {string} b - The second string to compare.
	 * @returns {number} -1 if the first string is less than the second string, 1 if the first string is greater than the second string, and 0 if the strings are equal.
	 */
	compareStrings(a: string, b: string) {
		const nameA = a.toUpperCase(); // ignore upper and lowercase
		const nameB = b.toUpperCase(); // ignore upper and lowercase
		if (nameA < nameB) {
			return -1;
		}
		if (nameA > nameB) {
			return 1;
		}

		// names must be equal
		return 0;
	},

	/**
	 * Rounds a value using a whole.
	 * @param {number} value - The value to round.
	 * @param {number} whole - The whole to round to.
	 * @param {string} wholeRoundOption - The option to round to.
	 * @returns {number} The rounded value.
	 */
	roundUsingWhole(value: number, whole: number, wholeRoundOption: string) {
		const wholeHasValue = this.hasValue(whole) && whole > 0;
		if (wholeHasValue && wholeRoundOption == 'EachWholeIncrement') {
			//round down by whole
			return util.roundDownToTheNearest(value, whole);
		} else if (wholeHasValue && wholeRoundOption == 'EachWholePortion') {
			// round up by whole
			return util.roundUpToTheNearest(value, whole);
		} else if (wholeHasValue && wholeRoundOption == 'NearestWholeMultiple') {
			// round up or down by whole
			return util.roundToTheNearest(value, whole);
		} else if (wholeHasValue && wholeRoundOption == 'NearestOddWhole') {
			// round up or down by whole x 2???
			return util.roundToTheNearestOdd(value, whole);
		} else {
			return Math.round((value + Number.EPSILON) * 100) / 100;
		}
	},

	/**
	 * Rounds a value to the nearest whole.
	 * @param {number} value - The value to round.
	 * @param {number} toTheNearest - The whole to round to.
	 * @returns {number} The rounded value.
	 */
	roundToTheNearest(value: number, toTheNearest: number) {
		return Math.round(value / toTheNearest) * toTheNearest;
	},

	/**
	 * Rounds a value up to the nearest whole.
	 * @param {number} value - The value to round.
	 * @param {number} toTheNearest - The whole to round to.
	 * @returns {number} The rounded value.
	 */
	roundUpToTheNearest(value: number, toTheNearest: number) {
		return Math.ceil(value / toTheNearest) * toTheNearest;
	},

	/**
	 * Rounds a value down to the nearest whole.
	 * @param {number} value - The value to round.
	 * @param {number} toTheNearest - The whole to round to.
	 * @returns {number} The rounded value.
	 */
	roundDownToTheNearest(value: number, toTheNearest: number) {
		return Math.floor(value / toTheNearest) * toTheNearest;
	},

	/**
	 * Rounds a value to the nearest odd whole.
	 * @param {number} value - The value to round.
	 * @param {number} toTheNearest - The whole to round to.
	 * @returns {number} The rounded value.
	 */
	roundToTheNearestOdd(value: number, toTheNearest: number) {
		return toTheNearest * 2 * Math.floor(value / (toTheNearest * 2)) + toTheNearest;
	},

	/**
	 * Encrypts a JSON text.
	 * @param {string} JSONText - The JSON text to encrypt.
	 * @returns {string} The encrypted JSON text.
	 */
	encryptJSONText(JSONText: string) {
		const byteArray = new TextEncoder().encode(JSONText);

		// btoa does not support UTF-8, so we use a binary string representation of JSONText
		let binaryString = '';
		for (let i = 0; i < byteArray.length; i++) {
			binaryString += String.fromCharCode(byteArray[i]);
		}

		return btoa(binaryString);
	},

	/**
	 * Decrypts a JSON text.
	 * @param {string} JSONTextEncrypted - The encrypted JSON text to decrypt.
	 * @returns {string} The decrypted JSON text.
	 */
	decryptJSONText(JSONTextEncrypted: string) {
		const binaryString = atob(JSONTextEncrypted);
		const byteArray = new Uint8Array(Array.from(binaryString, char => char.charCodeAt(0)));

		return new TextDecoder().decode(byteArray);
	},

	/**
	 * Converts a string to camel case.
	 * @param {string} str - The string to convert.
	 * @returns {string} The camel case string.
	 */
	camelCase(str: string) {
		return str
			.split(/[\W_]+/)
			.map((word: string, index: number) => (index === 0 ? word.charAt(0).toLowerCase() + word.slice(1) : word.charAt(0).toUpperCase() + word.slice(1)))
			.join('');
	},

	/**
	 * Formats a camel case string.
	 * @param {string} camelCaseStr - The camel case string to format.
	 * @returns {string} The formatted camel case string.
	 */
	formatCamelCase(camelCaseStr: string) {
		// Inserts spaces between each word
		return camelCaseStr.replace(/[A-Z]+(?![a-z])|[A-Z]/g, ' $&').trim();
	},

	/**
	 * Formats a snake case string.
	 * @param {string} snakeCaseStr - The snake case string to format.
	 * @returns {string} The formatted snake case string.
	 */
	formatSnakeCase(snakeCaseStr: string) {
		return snakeCaseStr.replaceAll('_', ' ').trim();
	},

	/**
	 * Converts a tax index to a tax rate.
	 * @param {number} taxIndex - The tax index to convert.
	 * @param {FIMenuBuyersOrderTaxGroup[]} taxGroups - The tax groups to convert.
	 * @returns {number} The tax rate.
	 */
	taxIndexToTaxRate(taxIndex: number, taxGroups: FIMenuBuyersOrderTaxGroup[]) {
		const taxIndexBinaryArray = util.taxIndexToBinaryArray(taxIndex);
		const taxRate = taxIndexBinaryArray
			.map((taxLine: string, taxLineIndex: number) => {
				if (taxLine == '1' && taxGroups[taxLineIndex]) return taxGroups[taxLineIndex].rate;
				else return 0;
			})
			.reduce((a, b) => a + b, 0);

		return taxRate;
	},

	/**
	 * Converts a tax index to a binary array.
	 * @param {number} taxIndex - The tax index to convert.
	 * @returns {string[]} The binary array.
	 */
	taxIndexToBinaryArray(taxIndex: number): string[] {
		const binaryStr = (taxIndex >>> 0).toString(2);
		const fullBinaryStr = '00000000'.substr(binaryStr.length) + binaryStr;
		const binaryArr = fullBinaryStr.split('');
		return binaryArr.reverse();
	},

	/**
	 * Converts a binary array to a tax index.
	 * @param {string[]} binaryArray - The binary array to convert.
	 * @returns {number} The tax index.
	 */
	binaryArrayToTaxIndex(binaryArray: string[]): number {
		return binaryArray.reverse().reduce((acc: number, val: string) => {
			return (acc << 1) | parseInt(val);
		}, 0);
	},

	/**
	 * Converts a binary array to a tax index.
	 * @param {number} taxIndex - The tax index to convert.
	 * @param {number} taxLineRemovedIndex - The tax line removed index to convert.
	 * @returns {number} The tax index.
	 */
	getNewTaxIndexIfYouRemoveATaxLine(taxIndex: number, taxLineRemovedIndex: number): number {
		const currentBinaryArray = util.taxIndexToBinaryArray(taxIndex);
		currentBinaryArray.splice(taxLineRemovedIndex, 1);
		currentBinaryArray.push('0');
		return this.binaryArrayToTaxIndex(currentBinaryArray);
	},

	/**
	 * Gets a random string.
	 * @param {number} length - The length of the string to get.
	 * @param {string} characters - The characters to use to generate the string.
	 * @returns {string} The random string.
	 */
	getRandomString(length: number, characters = 'abcdefghjkmnpqrtuvwxyABCDEFGHJKMNPQRTUVWXY346789'): string {
		let result = '';
		const charactersLength = characters.length;
		for (let i = 0; i < length; i++) {
			result += characters.charAt(Math.floor(Math.random() * charactersLength));
		}
		return result;
	},

	/**
	 * Gets a spireon account.
	 * @param {string} storeCode - The store code to get the spireon account for.
	 * @returns {PartnerCode} The spireon account.
	 */
	getSpireonAccount(storeCode: string) {
		return settings.userStores
			?.find((uS: Store) => uS.storeCode == storeCode)
			.partnerCodes.find((p: PartnerCode) => p.type === ENUMS.PARTNER_CODES.SPIREON_ACCOUNT_ID);
	},

	/**
	 * Gets the store state by store code.
	 * @param {string} storeCode - The store code to get the store state for.
	 * @returns {string} The store state.
	 */
	getStoreStateByStoreCode(storeCode: string) {
		return settings.userStores?.find((uS: Store) => uS.storeCode == storeCode)?.storeState;
	},

	/**
	 * Gets the carfax codes.
	 * @param {Store} userStore - The user store to get the carfax codes for.
	 * @returns {Promise<AxiosResponse<any>>} The carfax codes.
	 */
	getCarFaxCodes(userStore: Store) {
		const carfaxCodes = userStore.partnerCodes.filter(pC => {
			return pC.type === ENUMS.PARTNER_CODES.CAR_FAX_PARTNER_CODE || pC.type === ENUMS.PARTNER_CODES.CAR_FAX_UID_CODE;
		});
		const carFaxPartnerCode = carfaxCodes?.find(pC => pC.type === ENUMS.PARTNER_CODES.CAR_FAX_PARTNER_CODE)?.code;
		const carFaxUidCode = carfaxCodes?.find(pC => pC.type === ENUMS.PARTNER_CODES.CAR_FAX_UID_CODE)?.code;
		return { carFaxPartnerCode, carFaxUidCode };
	},

	/**
	 * Looks up a vin.
	 * @param {string} vin - The vin to look up.
	 * @param {string} storeCode - The store code to look up the vin for.
	 * @param {boolean} boolVinLookUp - Whether to look up the vin.
	 * @param {boolean} boolSearchCarFax - Whether to search carfax.
	 * @param {boolean} boolSearchLoJack - Whether to search lojack.
	 * @param {string} storeState - The store state to look up the vin for.
	 * @param {string} storeZip - The store zip to look up the vin for.
	 * @returns {Promise<AxiosResponse<any>>} The vin lookup results.
	 */
	vinLookup(
		vin: string,
		inventoryType: string,
		storeCode: string,
		boolVinLookUp: boolean,
		boolSearchCarFax: boolean,
		boolSearchLoJack: boolean,
		storeState: string,
		storeZip: string,
	) {
		const userStore = (settings.userStores as Store[])?.find(uS => uS.storeCode === storeCode);
		if (!userStore) throw new Error('Invalid store code.');

		const spireonAccount = userStore.partnerCodes.find(p => p.type === ENUMS.PARTNER_CODES.SPIREON_ACCOUNT_ID);

		const promises: Promise<AxiosResponse<any>>[] = [];

		if (boolVinLookUp) {
			promises.push(api.vehicle.vinLookup(vin, inventoryType));
		}

		if (boolSearchCarFax) {
			promises.push(api.carFax.DecodeCarFax(storeCode, vin));
		}
		if (boolSearchLoJack && spireonAccount) {
			promises.push(api.spireon.getAsset(vin, storeState, spireonAccount.code));
		}

		return Promise.all(promises);
	},

	/**
	 * Shows a carfax odometer warning.
	 * @param {number} lastOdometerReading - The last odometer reading.
	 * @param {number} vehicleMiles - The vehicle miles.
	 * @param {number} carFaxOdometerMaxOffset - The carfax odometer max offset.
	 * @param {boolean} isAdminView - Whether the view is admin.
	 * @param {FIMenu} fimenu - The fimenu.
	 */
	carFaxOdometerWarning(lastOdometerReading: number, vehicleMiles: number, carFaxOdometerMaxOffset: number, isAdminView: boolean, fimenu: FIMenu) {
		if (
			lastOdometerReading &&
			vehicleMiles != null &&
			carFaxOdometerMaxOffset != null &&
			Math.abs(parseInt(lastOdometerReading.toString().replace(',', '')) - vehicleMiles) > carFaxOdometerMaxOffset
		) {
			if (isAdminView) {
				//As Admin and CarFax Last Odo Wrong, Update with Vehicle Miles on Accept
				$modal.open(modalInfo, {
					name: 'modalInfo',
					passedData: {
						title: 'Warning',
						info: 'According to our records, the mileage on the vehicle is way off. Please verify the actual miles on the vehicles!',
						additionalData: fimenu,
						acceptText: 'Update Last Odometer',
					},
					backdrop: false,
					postFunction: (passedFimenu: FIMenu) => {
						passedFimenu.carFax.lastOdometerReading = passedFimenu.vehicleMiles;
					},
				});
			} else {
				$modal.open(modalInfo, {
					name: 'modalInfo',
					passedData: {
						title: 'Warning',
						info: 'According to our records, the mileage on the vehicle is way off. Please verify the actual miles on the vehicles!',
						acceptText: 'Accept',
					},
					backdrop: false,
					postFunction: () => {},
				});
			}
		}
	},

	/**
	 * Converts a string to proper case.
	 * @param {string} str - The string to convert.
	 * @returns {string} The proper case string.
	 */
	toProperCase(str: string) {
		if (!this.hasValue(str)) {
			console.warn('toProperCase invalid string input', str);
			return '';
		}
		return str.replace(/\w\S*/g, function (txt: string) {
			return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
		});
	},

	/**
	 * Converts a camel case string to a proper case string.
	 * @param {string} str - The string to convert.
	 * @returns {string} The proper case string.
	 */
	camelToProperCase(str: string) {
		// Insert spaces before uppercase letters
		const spacedString = str.replace(/([A-Z])/g, ' $1').trim();

		// Convert to title case
		return spacedString
			.toLowerCase()
			.split(' ')
			.map(word => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	},

	/**
	 * Gets a guid.
	 * @returns {string} The guid.
	 */
	getGuid() {
		return crypto.randomUUID();
	},

	/**
	 * Checks if a value is a valid guid.
	 * @param {string} val - The value to check.
	 * @returns {boolean} Whether the value is a valid guid.
	 */
	isAValidGuid(val: string) {
		return val !== 'undefined' && val !== null && val !== '00000000-0000-0000-0000-000000000000';
	},

	/**
	 * Gets the default guid.
	 * @returns {string} The default guid.
	 */
	getDefaultGuid(): string {
		return '00000000-0000-0000-0000-000000000000';
	},

	/**
	 * Checks if a date is in the future.
	 * @param {SaturnDate} date - The date to check.
	 * @returns {boolean} Whether the date is in the future.
	 */
	isFutureDate(date: SaturnDate) {
		const currentDate = new Date();
		if (typeof date === 'string') {
			date = new Date(date);
		}
		return date > currentDate;
	},

	/**
	 * Checks if a date is in the past.
	 * @param {SaturnDate} date - The date to check.
	 * @returns {boolean} Whether the date is in the past.
	 */
	isPastDate(date: SaturnDate) {
		const currentDate = new Date();
		// Set the time of currentDate to midnight to only compare the date part
		currentDate.setHours(0, 0, 0, 0);
		// Set the time of the input date to midnight to only compare the date part
		const inputDate = moment(date).toDate();
		inputDate.setHours(0, 0, 0, 0);

		return inputDate < currentDate;
	},

	/**
	 * Converts a date to a moment object.
	 * @param {SaturnDate} dateStr - The date to convert.
	 * @param {string[]} format - The format to convert the date to.
	 * @returns {Moment} The moment object.
	 */
	toMoment(dateStr: SaturnDate, format = ['MM/DD/YYYY', 'YYYY-MM-DDTHH:mm:ss.SSSZ']) {
		const toMoment = moment(dateStr, format).isValid() ? moment(dateStr, format) : moment(moment(dateStr), format);
		return toMoment;
	},

	/**
	 * Converts a date to a moment object in UTC.
	 * @param {SaturnDate} dateStr - The date to convert.
	 * @param {string[]} format - The format to convert the date to.
	 * @returns {Moment} The moment object in UTC.
	 */
	toMomentUtc(dateStr: SaturnDate, format = ['MM/DD/YYYY', 'YYYY-MM-DDTHH:mm:ss.SSSZ']) {
		return moment.utc(dateStr, format);
	},

	/**
	 * Adds days to a moment object.
	 * @param {SaturnDate} dateStr - The date to add days to.
	 * @param {number} daysToAdd - The number of days to add.
	 * @param {boolean} excludeWeekends - Whether to exclude weekends.
	 * @param {boolean} utc - Whether the date is in UTC.
	 * @returns {Moment} The moment object with the added days.
	 */
	addDaysToMoment(dateStr: SaturnDate, daysToAdd: number, excludeWeekends = false, utc = false) {
		const momentDate = utc ? this.toMomentUtc(dateStr) : this.toMoment(dateStr); // Ensure date is a moment object

		if (!momentDate.isValid()) return null; // Handle invalid dates

		let addedDays = 0;

		while (addedDays < daysToAdd) {
			momentDate.add(1, 'days'); // Move forward by one day

			if (excludeWeekends) {
				// Skip Saturdays (6) and Sundays (0)
				if (momentDate.day() !== 0 && momentDate.day() !== 6) {
					addedDays++;
				}
			} else {
				addedDays++;
			}
		}

		return momentDate;
	},

	/**
	 * Converts a date to a date object.
	 * @param {SaturnDate} date - The date to convert.
	 * @returns {Date} The date object.
	 */
	convertToDate(date: SaturnDate) {
		if (date instanceof Date) {
			return date;
		} else {
			return this.toMoment(date).toDate();
		}
	},

	/**
	 * Converts an hour to AM/PM time.
	 * @param {number} hour - The hour to convert.
	 * @returns {string} The AM/PM time.
	 */
	convertToAMPM(hour: number) {
		return new Date(0, 0, 0, hour).toLocaleTimeString('en-US', {
			hour: 'numeric',
			hour12: true,
		});
	},

	/**
	 * Gets validators.
	 * @param {Record<string, Record<string, unknown>>[]} objectsList - The objects list to get the validators for.
	 * @returns {Record<string, Record<string, unknown>>} The validators.
	 */
	getValidators(objectsList: Record<string, Record<string, unknown>>[]) {
		const validation: Record<string, Record<string, unknown>> = {};
		//typeof defaultValidators & typeof customValidators
		const allValidators: Record<string, unknown> = {
			...defaultValidators,
			...customValidators,
		};

		objectsList.forEach(object => {
			if (Array.isArray(object.validation)) {
				object.validation.forEach(
					({
						propertyName,
						validators,
					}: {
						propertyName: keyof typeof validation;
						validators: Array<{
							type: keyof typeof allValidators;
							params: unknown;
						}>;
					}) => {
						validation[propertyName] = {};

						//type: keyof typeof allValidators
						validators.forEach(({ type, params }: { type: keyof typeof allValidators; params: unknown }) => {
							if (typeof allValidators[type] === 'function' && Array.isArray(params)) {
								validation[propertyName][type] = allValidators[type](...params);
							} else {
								validation[propertyName][type] = allValidators[type];
							}
						});
					},
				);
			}
		});

		return validation;
	},

	// TODO: REVIEW THIS ONE
	/**
	 * Gets a property value from an object.
	 * @param {T} object - The object to get the property value from.
	 * @param {string} pathString - The path to the property.
	 * @returns {T} The property value.
	 */
	getPropValueRaw<T>(object: T, pathString: string) {
		const updateKey = (property: keyof T) => {
			if (property.toString().includes('[')) {
				const propertyPieces = property.toString().split('[');

				property = propertyPieces[0] as keyof T;
				object = object[property] as T;
				property = propertyPieces.pop().split(']')[0] as keyof T;
			}

			if (object) {
				object = object[property] as T;
			}
		};

		pathString.split('.').forEach(p => {
			if (object) {
				updateKey(p as keyof T);
			}
		});

		return object;
	},

	/**
	 * Full US Zip Code Format xxxxx-xxxx
	 * @returns {string} The onkeypress zip code format.
	 */
	getOnkeypressZipCodeFormat(): string {
		return 'return (event.charCode !=8 && event.charCode ==0 || (event.charCode >= 48 && event.charCode <= 57) || (event.charCode == 45))';
	},

	/**
	 * Outside US Zip Code (allows numbers, letters, spaces, and dashes)
	 * @returns {string} The onkeypress outside US zip code format.
	 */
	getOnkeypressOutsideUSZipCodeFormat(): string {
		return 'return (event.charCode !=8 && event.charCode ==0 || (event.charCode >= 48 && event.charCode <= 57) || (event.charCode >= 65 && event.charCode <= 90) || (event.charCode >= 97 && event.charCode <= 122) || (event.charCode == 32) || (event.charCode == 45))';
	},

	/**
	 * County Format (allows letters, spaces, dashes, slashes, and periods)
	 * @returns {string} The onkeypress county format.
	 */
	getOnkeypressCountyFormat(): string {
		return 'return (event.charCode == 32 || event.charCode == 45 || event.charCode == 47 || event.charCode == 46 || (event.charCode >= 65 && event.charCode <= 90) || (event.charCode >= 97 && event.charCode <= 122));';
	},

	/**
	 * Numbers Only Format (allows numbers)
	 * @returns {string} The onkeypress numbers only format.
	 */
	getOnkeypressNumbersOnlyFormat(): string {
		return 'return (event.charCode !=8 && event.charCode ==0 || (event.charCode >= 48 && event.charCode <= 57))';
	},

	/**
	 * Number and Decimals Format (allows numbers and decimals)
	 * @returns {string} The onkeypress number and decimals format.
	 */
	getOnkeypressNumberAndDecimalsFormat(): string {
		return 'return (event.charCode !=8 && event.charCode ==0 || ( event.charCode == 46 || (event.charCode >= 48 && event.charCode <= 57)))';
	},

	/**
	 * Outside US Zip Code (allows numbers, letters, spaces, and dashes)
	 * @returns {RegExp} The at input method outside US zip code format.
	 */
	getAtInputMethodOutsideUSZipCodeFormat(): RegExp {
		return /[^a-zA-Z0-9\s-]/g;
	},

	/**
	 * Number Only Format (allows numbers)
	 * @returns {RegExp} The at input method number only format.
	 */
	getAtInputMethodNumbersOnlyFormat(): RegExp {
		return /[^0-9]/g;
	},

	/**
	 * County Format (allows letters, spaces, dashes, slashes, and periods)
	 * @returns {RegExp} The at input method county format.
	 */
	getAtInputMethodCountyFormat(): RegExp {
		return /[^a-zA-Z\s\-/.]/g;
	},

	/**
	 * Gets a key from a value.
	 * @param {T} object - The object to get the key from.
	 * @param {unknown} value - The value to get the key from.
	 * @returns {string} The key.
	 */
	getKeyFromValue<T>(object: T, value: unknown) {
		return Object.keys(object).find(key => object[key as keyof T] === value);
	},

	//TODO TEST THIS
	//parameter method must not be annonymous
	/**
	 * Debounces a method.
	 * @param {T} method - The method to debounce.
	 * @param {number} delay - The delay.
	 * @param {...Parameters<T>} args - The arguments.
	 */
	debounce<T extends (...args: any[]) => void>(method: T, delay: number, ...args: Parameters<T>) {
		if ((method as any)._tId !== null) {
			clearTimeout((method as any)._tId);
			(method as any)._tId = null;
		}

		(method as any)._tId = setTimeout(function () {
			// method.apply(null, args);
			method(...args);
		}, delay);
	},

	//TODO TEST THIS
	/**
	 * Debounces a method and returns a promise.
	 * @param {T} method - The method to debounce.
	 * @param {number} delay - The delay.
	 * @param {...Parameters<T>} args - The arguments.
	 * @returns {Promise<ReturnType<T>>} The promise.
	 */
	debounceReturn<T extends (...args: any[]) => ReturnType<T>>(method: T, delay: number, ...args: Parameters<T>) {
		if ((method as any)._tId !== null) {
			clearTimeout((method as any)._tId);
			(method as any)._tId = null;
		}

		return new Promise(resolve => {
			(method as any)._tId = setTimeout(() => {
				// const result = method.apply(null, args);
				const result = method(...args);
				resolve(result);
			}, delay);
		});
	},

	/**
	 * Gets the word plural.
	 * @param {string} wordSingular - The word singular.
	 * @param {string} wordPlural - The word plural.
	 * @param {number} amount - The amount.
	 * @returns {string} The word plural.
	 */
	getWordPlural(wordSingular: string, wordPlural: string, amount: number) {
		return amount == 1 ? wordSingular : wordPlural;
	},

	/**
	 * Calculates the monthly income.
	 * @param {string} frequency - The frequency.
	 * @param {number} amount - The amount.
	 * @returns {number} The monthly income.
	 */
	calculateMonthlyIncome(frequency: 'weekly' | 'biWeekly' | 'annually' | 'quarterly' | 'semiAnnual', amount: number) {
		switch (frequency) {
			case 'weekly':
				return (amount * 52) / 12;

			case 'biWeekly':
				return (amount * 26) / 12;

			case 'annually':
				return amount / 12;

			case 'quarterly':
				return (amount * 4) / 12;

			case 'semiAnnual':
				return (amount * 2) / 12;

			default:
				return amount;
		}
	},

	/**
	 * Converts a file to a base64 string.
	 * @param {File} file - The file to convert.
	 * @returns {Promise<string>} The base64 string.
	 */
	fileToBase64(file: File) {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();

			reader.onload = () => {
				const arrayBuffer = reader.result as ArrayBuffer;
				const byteArray = new Uint8Array(arrayBuffer);
				let base64String = '';

				for (let i = 0; i < byteArray.length; i++) {
					base64String += String.fromCharCode(byteArray[i]);
				}

				resolve(btoa(base64String));
			};

			reader.onerror = reject;

			reader.readAsArrayBuffer(file);
		});
	},

	/**
	 * Sends a text message.
	 * @param {string} storeCode - The store code.
	 * @param {string} phoneNumber - The phone number.
	 * @param {string} message - The message.
	 * @returns {Promise<any>} The response.
	 */
	async sendTextMessage(storeCode: string, phoneNumber: string, message: string) {
		// Phone number needs to be in e.164 format (ex: +12345678909)
		if (phoneNumber.search(/^\+[1-9]\d{1,14}$/gm) === -1) {
			console.error('Cannot send text message. Provided phone number was not in e.164 format.');
			return;
		}

		const messageRequest = {
			toPhoneNumber: phoneNumber,
			body: message,
			storeCode: storeCode,
		};

		const response = await api.utilities.sendMessage(messageRequest);
		return response;
	},

	/**
	 * Formats a path.
	 * @param {string} str - The path to format.
	 * @returns {string} The formatted path.
	 */
	formatPath(str: string): string {
		//if str it not a valid path, return it.
		if (!str || !str.includes('.')) return str;

		return str
			.replace(/([A-Z])/g, ' $1')
			.split('.')
			.map(word => {
				return word.charAt(0).toUpperCase() + word.slice(1);
			})
			.join(' ');
	},

	/**
	 * Formats a path for accounting.
	 * @param {string} path - The path to format.
	 * @param {boolean} removeTrailingPeriod - Whether to remove the trailing period.
	 * @returns {string} The formatted path.
	 */
	formatForAccounting(path: string, removeTrailingPeriod = false): string {
		if (!path) {
			return;
		}
		path = path.replace('buyersOrders.finalNumbers.leaseFinalNumbers', 'lease');

		if (removeTrailingPeriod) {
			path = path.replace('buyersOrders.finalNumbers.', '');
			path = path.replace('buyersOrders.', '');
		} else {
			//initial clean up
			path = path.replace('buyersOrders.finalNumbers', '');
			path = path.replace('buyersOrders', '');
		}

		return this.formatPath(path);
	},

	/**
	 * Gets the store timezone.
	 * @param {string} timezone - The timezone.
	 * @returns {string} The store timezone.
	 */
	getStoreTimezone(timezone: string): string {
		const identifiers: { csharpIdentifier: string; jsIdentifier: string }[] = [
			{
				csharpIdentifier: 'Eastern Standard Time',
				jsIdentifier: 'America/New_York',
			},
			{
				csharpIdentifier: 'Central Standard Time',
				jsIdentifier: 'America/Chicago',
			},
			{
				csharpIdentifier: 'Mountain Standard Time',
				jsIdentifier: 'America/Denver',
			},
			{
				csharpIdentifier: 'Pacific Standard Time',
				jsIdentifier: 'America/Los_Angeles',
			},
			{
				csharpIdentifier: 'Alaskan Standard Time',
				jsIdentifier: 'America/Anchorage',
			},
			{
				csharpIdentifier: 'Hawaiian Standard Time',
				jsIdentifier: 'Pacific/Honolulu',
			},
		];

		return identifiers.find(identifier => identifier.csharpIdentifier == timezone)?.jsIdentifier;
	},

	/**
	 * Gets the vue ref to watch.
	 * @param {ComponentPublicInstance} vueRef - The vue ref to watch.
	 * @returns {Record<string, unknown>} The vue ref to watch.
	 */
	getVueRefToWatch(vueRef: ComponentPublicInstance) {
		// Create methods.
		const methods: Record<string, unknown> = {};

		if (vueRef?.$options?.methods) {
			Object.keys(vueRef.$options.methods).forEach(k => {
				methods[k] = vueRef[k as keyof typeof vueRef];
			});
		}

		return { data: vueRef.$data, methods };
	},

	/**
	 * Converts a base64 PDF to a Uint8Array.
	 * @param {string} base64Pdf - The base64 PDF to convert.
	 * @returns {Uint8Array} The Uint8Array.
	 */
	base64ToUint8Array(base64Pdf: string): Uint8Array {
		const fileBase64 = base64Pdf;

		// Convert the base64 PDF to a Blob
		const byteCharacters = atob(fileBase64);
		const byteNumbers = new Array(byteCharacters.length);
		for (let i = 0; i < byteCharacters.length; i++) {
			byteNumbers[i] = byteCharacters.charCodeAt(i);
		}
		return new Uint8Array(byteNumbers);
	},

	/**
	 * Converts a Uint8Array to a base64 string.
	 * @param {Uint8Array} uint8Array - The Uint8Array to convert.
	 * @returns {string} The base64 string.
	 */
	uint8ArrayToBase64(uint8Array: Uint8Array): string {
		let binaryString = '';
		for (let i = 0; i < uint8Array.length; i++) {
			binaryString += String.fromCharCode(uint8Array[i]); // Convert each byte to a string
		}
		return btoa(binaryString); // Convert binary string to base64
	},

	/**
	 * Converts a base64 string to a Blob.
	 * @param {string} file - The base64 string to convert.
	 * @param {string} fileType - The file type.
	 * @returns {Blob} The Blob.
	 */
	base64ToBlob(file: string, fileType: string): Blob {
		const byteArray = this.base64ToUint8Array(file);
		const blobFile = new Blob([byteArray], { type: fileType });

		return blobFile;
	},

	/**
	 * Downloads a Blob.
	 * @param {Blob} file - The Blob to download.
	 * @param {string} fileName - The file name.
	 */
	downloadBlob(file: Blob, fileName: string) {
		// Takes in a Blob. If you have a base64 file; you may convert it with base64ToBlob.
		try {
			// Create a data URL for the Blob
			const dataURL = URL.createObjectURL(file);

			// Create a download link
			const link = document.createElement('a');
			document.body.appendChild(link); // Append link to the body
			link.style.display = 'none'; // Hide the link
			link.href = dataURL;
			link.download = fileName;

			// Trigger the download
			link.click();

			// Clean up by removing link and revoking URL
			setTimeout(() => {
				document.body.removeChild(link);
				window.URL.revokeObjectURL(dataURL);
			}, 100);
		} catch (error) {
			console.error('Error downloading PDF:', error);
			throw error; // Propagate the error to the caller
		}
	},

	/**
	 * Prints a Blob.
	 * @param {Blob} file - The Blob to print.
	 */
	printBlob(file: Blob) {
		const pdfDataUrl = URL.createObjectURL(file);
		const iframe = document.createElement('iframe');
		// Set the source of the iframe to the data URL
		iframe.src = pdfDataUrl;
		// Set the iframe to be hidden
		iframe.style.display = 'none';
		// Append the iframe to the body
		document.body.appendChild(iframe);
		// Wait for the iframe to load before triggering the print
		iframe.onload = function () {
			// Trigger the print
			iframe.contentWindow.print();
		};
	},

	/**
	 * Extracts a number from a string.
	 * @param {string} text - The string to extract the number from.
	 * @returns {number} The number.
	 */
	extractNumber: (text: string): number => {
		const match = text.match(/\d+/); // Extract the first sequence of digits
		return match ? parseInt(match[0], 10) : 0; // Convert the match to an integer, default to 0 if no match
	},

	/**
	 * Method that will open a modal with information about why the deal is locked
	 * If the user clicks cancel, nothing happens. If they click confirm/ok. It'll execute post function and close itself.
	 * @param {*} message  Message/Parragraph to display as text into the modal. Could be text or array of text.
	 * @param {*} postFunction Function to execute if user clicks acceptText
	 * @param {*} title Modal title
	 * @param {*} acceptText Confirm button by default is Ok
	 */

	modalCopyDeal: (postFunction = (storeCode: string) => {}, cancelFunction = () => {}) => {
		$modal.open(modalCopyDeal, {
			name: 'modalCopyDeal',
			passedData: {},
			backdrop: false,
			postFunction,
			cancelFunction,
		});
	},

	/**
	 * Method that will open a modal with information about why the deal is locked
	 * If the user clicks cancel, nothing happens. If they click confirm/ok. It'll execute post function and close itself.
	 * @param {*} message  Message/Parragraph to display as text into the modal. Could be text or array of text.
	 * @param {*} postFunction Function to execute if user clicks acceptText
	 * @param {*} title Modal title
	 * @param {*} acceptText Confirm button by default is Ok
	 */

	modalLockedMessage: (
		message: string,
		acceptText: string = 'Yes',
		postFunction: GenericFunction = () => {},
		cancelText: string = 'No',
		cancelFunction: GenericFunction = () => {},
		title: string = null,
		fitContent: boolean = true,
		lockType: string = null,
	) => {
		$modal.open(modalLockedMessage, {
			name: 'modalLockedMessage',
			passedData: {
				info: message,
				acceptText,
				cancelText,
				title,
				cancelTextClass: 'button-unwind fixedShort',
				acceptTextClass: 'button-save fixedShort',
				fitContent,
				lockType,
			},
			backdrop: false,
			postFunction,
			cancelFunction,
		});
	},

	/**
	 * Mehtod that will open a modal with information to confirm.
	 * If the user clicks cancel, nothing happens. If they click confirm/ok. It'll execute post function and close itself.
	 * @param {*} message  Message/Parragraph to display as text into the modal. Could be text or array of text.
	 * @param {*} postFunction Function to execute if user clicks acceptText
	 * @param {*} title Modal title
	 * @param {*} acceptText Confirm button by default is Ok
	 */

	modalConfirmation: (
		message: string,
		acceptText: string = 'Yes',
		postFunction: GenericFunction = () => {},
		cancelText: string = 'No',
		cancelFunction: GenericFunction = () => {},
		title: string = null,
		fitContent: boolean = true,
		additionalInfo: string = null,
	) => {
		$modal.open(modalInfo, {
			name: 'modalInfo',
			passedData: {
				info: message,
				acceptText,
				cancelText,
				title,
				cancelTextClass: 'button-unwind fixedShort',
				acceptTextClass: 'button-save fixedShort',
				fitContent,
				additionalInfo,
			},
			backdrop: false,
			postFunction,
			cancelFunction,
		});
	},

	/**
	 * Mehtod that will open a modal with information to confirm.
	 * Once the user clicks confirm/ok. It'll execute post function and close itself.
	 * @param {*} message  Message/Parragraph to display as text into the modal. Could be text or array of text.
	 * @param {*} postFunction Function to execute if user clicks @acceptText
	 * @param {*} title Modal title
	 * @param {*} acceptText Confirm button by default is "Ok"
	 */
	modalInformation: (message: string, postFunction: GenericFunction = () => {}, title: string = null, acceptText: string = 'Ok', fitContent: boolean = true) => {
		$modal.open(modalInfo, {
			name: 'modalInfo',
			passedData: {
				info: message,
				acceptText,
				title,
				acceptTextClass: 'button-save fixedShort',
				fitContent,
			},
			backdrop: false,
			postFunction: postFunction,
		});
	},
	/**
	 * This method creates a modal with the inputs passed in @inputs
	 * AcceptButton will not do anything but close the modal as it modifies the property itself.
	 * postFunction will execute before closing modal.
	 * @param {*} inputData The type looks like following: [{label, value, disabled, placeholder}]
	 * @param {*} message Any message to display prior to the input fields. Could be text or array of text.
	 * @param {*} title Modal title
	 * @param {*} acceptText Confirm button by default is "Save & Close"
	 * @param {*} postFunction Function to execute if user clicks @acceptText
	 */
	modalInputs: (
		input = {},
		message: string,
		title: string = null,
		postFunction: GenericFunction = () => {},
		acceptText: string = 'Save & Close',
		cancelText: string = null,
	) => {
		$modal.open(modalInputs, {
			name: 'modalInputs',
			passedData: {
				info: message,
				acceptText,
				title,
				input,
				acceptTextClass: 'button-save fixedShort',
				cancelText,
			},
			backdrop: false,
			postFunction,
		});
	},
	/**
	 * For documents to be uploaded to Azure's File Share. Formats a filename so it won't contain any illegal characters or exceed
	 * the character limit. Throws an error if the filename is an Azure reserve word.
	 * @param {String} fileName The name of the file to format
	 * @param {String} fileType A MIME type string
	 * @returns The Azure-safe filename
	 */
	formatFileNameForAzure(fileName: string, fileType: string) {
		if (/^((LPT|COM)[1-9]|PRN|AUX|NUL|CON|CLOCK\$|\.|\.\.)$/.test(fileName)) throw new Error('Cannot use Azure reserve words as file name.');

		fileName = fileName.replaceAll(/[^a-zA-Z0-9\-._~]/g, '-');

		// TODO: This only works for simple MIME types. When we start supporting more filetypes, would probably
		// have to replace this with an explicit mapping.
		const fileExtension = fileType === 'text/plain' ? 'txt' : fileType.substring(fileType.indexOf('/') + 1);

		// Filename needs to end with the extension to ensure it can be redownloaded correctly
		if (!fileName.endsWith(`.${fileExtension}`)) fileName += `.${fileExtension}`;

		// Truncate filename if needed without removing the file extension
		if (fileName.length > 255) {
			const amountToCut = 255 - `.${fileExtension}`.length;
			fileName = `${fileName.substring(0, amountToCut)}.${fileExtension}`;
		}

		return fileName;
	},

	/**
	 * Converts a phrase to proper case.
	 * @param {string} phrase - The phrase to convert.
	 * @returns {string} The proper case phrase.
	 */
	getProperCasePhrase(phrase: string) {
		return phrase.toLowerCase().replace(/(?:^|\s)\w/g, function (match) {
			return match.toUpperCase();
		});
	},

	/**
	 * Gets the route ready.
	 * @param {ComponentPublicInstance & { $route: RouteLocationNormalizedLoaded }} componentInstance - The component instance.
	 * @returns {Promise<boolean>} The route ready.
	 */
	async getRouteReady(
		componentInstance: ComponentPublicInstance & {
			$route: RouteLocationNormalizedLoaded;
		},
	) {
		return new Promise((resolve, reject) => {
			// Define the function that checks the route readiness
			const checkRoute = () => {
				if (componentInstance.$route.name) {
					resolve(true); // If the route is ready, resolve the promise
				} else {
					setTimeout(checkRoute, 50); // Otherwise, retry after a delay
				}
			};

			checkRoute(); // Initiate the first check
		});
	},

	/**
	 * Checks if the screen is mobile.
	 * @returns {boolean} Whether the screen is mobile.
	 */
	isMobile() {
		return window.innerWidth <= 533;
	},

	/**
	 * Sets a query parameter without causing a re-render.
	 * You WONT have access to this query parameters until you refresh
	 * @param {string} queryParam - The query parameter to set.
	 * @param {string} queryValue - The value to set the query parameter to.
	 * @param {Router} $router - The router.
	 */
	setQueryParm(queryParam: string, queryValue: string, $router: Router | null = null) {
		const urlSearchParams = new URLSearchParams(window.location.search);
		urlSearchParams.set(queryParam, queryValue);
		const newUrl = window.location.pathname + '?' + urlSearchParams.toString();

		$router?.push(newUrl); //FIXME: The router should be used to better handle navigation instead of directly modifying the window object.
		window.history.replaceState({}, '', newUrl);
	},

	/**
	 * Removes a query parameter without causing a re-render.
	 * You WONT have access to this query parameters until you refresh
	 * @param {string} queryParam - The query parameter to remove.
	 */
	deleteQueryParam(queryParam: string) {
		const urlSearchParams = new URLSearchParams(window.location.search);

		urlSearchParams.delete(queryParam);

		const newUrl = window.location.pathname + '?' + urlSearchParams.toString();
		window.history.replaceState({}, '', newUrl);
	},

	/**
	 * Appends the scope class to all style rules inside of the style element.
	 * @param {HTMLStyleElement} styleElem
	 * @param {string} scopeSelector a valid CSS selector to append to all styles
	 * @return the scoped inner text of the style element
	 */
	getScopedStyles(styleElem: HTMLStyleElement, scopeSelector: string) {
		let newStyles = styleElem.innerHTML.replaceAll(/([^\r\n,{}]+)(,(?=[^}]*{)|\s*{)/gm, ` ${scopeSelector} $1$2`);

		// Any @-rules that got accidentally modified get reverted back to how they originally were
		const escapedSelector = scopeSelector.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
		newStyles = newStyles.replaceAll(new RegExp(`(${escapedSelector})\\s*@`, 'g'), '@');
		return newStyles;
	},

	/**
	 * Caches a deal locally.
	 * @param {string} dealNumber - The deal number.
	 * @param {FIMenuDataModelBase} testModel - The test model.
	 * @param {DataModel} modelType - The model type.
	 */
	cacheDealLocally<T extends FIMenuDataModelBase>(dealNumber: string, testModel: T, modelType: DataModel = null) {
		// console.log("Caching:", dealNumber, testModel);
		const noData = !(dealNumber && testModel);
		if (noData) {
			return;
		}

		localStorage.setItem(
			'cachedDealInfo',
			JSON.stringify({
				dealNumber: dealNumber,
				storeCode: testModel.store?.storeCode,
			}),
		);
		localStorage.setItem('dealTestModel', JSON.stringify({ testModel, modelType }));
	},

	/**
	 * Sets a cookie.
	 * @param {string} name - The name of the cookie.
	 * @param {string} value - The value of the cookie.
	 * @param {number} days - The number of days to set the cookie for.
	 */
	setCookie(name: string, value: string, days: number) {
		const date = new Date();
		date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
		const expires = 'expires=' + date.toUTCString();
		document.cookie = name + '=' + value + ';' + expires + ';path=/';
	},

	/**
	 * Gets a cookie.
	 * @param {string} name - The name of the cookie.
	 * @returns {string | null} The value of the cookie.
	 */
	getCookie(name: string) {
		const nameEQ = name + '=';
		const ca = document.cookie.split(';');
		for (let i = 0; i < ca.length; i++) {
			let c = ca[i];
			while (c.charAt(0) === ' ') c = c.substring(1);
			if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
		}
		return null;
	},

	/**
	 * Checks if a value is a non-null object (and not an array).
	 * @param {unknown} value - The value to check.
	 * @returns {boolean} Whether the value is an object.
	 */
	isObject(value: unknown): value is Record<string, unknown> {
		return !!value && typeof value === 'object' && !Array.isArray(value);
	},

	/**
	 * Alphabetizes the keys of a JavaScript object.
	 * @param {any | any[]} obj - The object to alphabetize.
	 * @returns {any | any[]} The alphabetized object.
	 */
	alphabetizeKeys(obj: any | any[]): any | any[] {
		if (Array.isArray(obj)) {
			const newArr: any[] = [];
			obj.forEach((item, idx) => {
				newArr.push(util.alphabetizeKeys(item));
			});
			return newArr;
		} else if (util.isObject(obj)) {
			const keys = Object.keys(obj).sort();
			const sortedObj: Record<string, any> = {};

			keys.forEach((key: keyof typeof obj) => {
				sortedObj[key] = util.alphabetizeKeys(obj[key]);
			});

			return sortedObj;
		} else {
			return obj;
		}
	},

	/**
	 * Sorts an array by a key.
	 * @param {T[]} array - The array to sort.
	 * @param {GenericFunction} keyExtractor - The key extractor.
	 * @param {'asc' | 'desc'} order - The order to sort the array in.
	 * @returns {T[]} The sorted array.
	 */
	sortBy<T>(array: T[], keyExtractor: GenericFunction, order: 'asc' | 'desc' = 'asc') {
		return array.sort((a, b) => {
			const keyA = keyExtractor(a);
			const keyB = keyExtractor(b);

			let result = 0;
			if (typeof keyA === 'string' && typeof keyB === 'string') {
				result = keyA.localeCompare(keyB);
			} else {
				result = keyA > keyB ? 1 : keyA < keyB ? -1 : 0;
			}

			return order === 'desc' ? -result : result;
		});
	},

	/**
	 * Maps a employee position into ENUMS.DEAL_EMPLOYEE_ROLE_TYPES. If a position is not mapped to a deal role, returns null.
	 * @param {string} employeePosition - The employee position to convert.
	 * @returns {DealEmployeeRoleTypes | null} The deal role.
	 */
	convertPositionToDealRole(employeePosition: string) {
		switch (employeePosition.toUpperCase()) {
			case 'SALES PERSON':
				return ENUMS.DEAL_EMPLOYEE_ROLE_TYPES.SALES_PERSON1;
			case 'SALES MANAGER':
				return ENUMS.DEAL_EMPLOYEE_ROLE_TYPES.SALES_MANAGER;
			case 'F&I':
			case 'FI MANAGER':
				return ENUMS.DEAL_EMPLOYEE_ROLE_TYPES.FI_MANAGER;
			case 'COSTING CLERK':
				return ENUMS.DEAL_EMPLOYEE_ROLE_TYPES.COSTING_CLERK;
			case 'F&I ADMIN':
				return ENUMS.DEAL_EMPLOYEE_ROLE_TYPES.RIPPER;
			case 'CONTROLLER':
				return ENUMS.DEAL_EMPLOYEE_ROLE_TYPES.CONTROLLER;
			default:
				return null;
		}
	},

	/**
	 * Maps ENUMS.DEAL_EMPLOYEE_ROLE_TYPES to an employee position. If a deal role is not mapped to a position, returns null.
	 * @param {DealEmployeeRoleTypes} dealRole - The deal role to convert.
	 * @returns {string | null} The employee position.
	 */
	convertDealRoleToPosition(dealRole: DealEmployeeRoleTypes) {
		switch (dealRole) {
			case ENUMS.DEAL_EMPLOYEE_ROLE_TYPES.SALES_PERSON1:
			case ENUMS.DEAL_EMPLOYEE_ROLE_TYPES.SALES_PERSON2:
				return 'SALES PERSON';
			case ENUMS.DEAL_EMPLOYEE_ROLE_TYPES.SALES_MANAGER:
				return 'SALES MANAGER';
			case ENUMS.DEAL_EMPLOYEE_ROLE_TYPES.FI_MANAGER:
				return 'F&I';
			case ENUMS.DEAL_EMPLOYEE_ROLE_TYPES.COSTING_CLERK:
				return 'COSTING CLERK';
			case ENUMS.DEAL_EMPLOYEE_ROLE_TYPES.RIPPER:
				return 'F&I ADMIN';
			case ENUMS.DEAL_EMPLOYEE_ROLE_TYPES.CONTROLLER:
				return 'CONTROLLER';
			default:
				return null;
		}
	},

	/**
	 * Converts an enum object to a plain object where the keys are the enum keys and the values are the enum values.
	 * @param {Record<string, T>} enumObj - The enum object to convert.
	 * @returns {Record<string, number>} A plain object where the keys are the enum keys and the values are the enum values.
	 */
	convertEnumToObject<T>(enumObj: Record<string, T>) {
		const result: Record<string, number> = {};

		for (const key in enumObj) {
			if (typeof enumObj[key] === 'number') {
				result[key] = enumObj[key];
			}
		}
		return result;
	},

	/**
	 * Validates a color.
	 * @param {string} color - The color to validate.
	 * @returns {string} The validated color.
	 */
	styleBindColorValidation(color: string) {
		if (color.startsWith('--')) {
			return `var(${color})`;
		}
		const style = new Option().style;
		style.color = color;
		if (style.color !== '') {
			return color;
		}

		return 'inherit';
	},

	/**
	 * Validates a size.
	 * @param {string} size - The size to validate.
	 * @param {Record<string, string> | null} defaultSizes - The default sizes.
	 * @returns {string} The validated size.
	 */
	styleBindSizeValidation(size: string, defaultSizes: Record<string, string> = null) {
		if (defaultSizes && size) {
			const sizeFromDefault = defaultSizes[size as keyof typeof defaultSizes];
			if (sizeFromDefault) {
				return sizeFromDefault;
			}
		}

		const sizeToInt = parseInt(size);
		if (Number.isInteger(sizeToInt)) {
			return `${sizeToInt}px`;
		}

		return 'inherit';
	},

	/**
	 * Formats an enum key.
	 * @param {string} enumKey - The enum key to format.
	 * @param {boolean} capitalizeFirstLetter - Whether to capitalize the first letter.
	 * @returns {string} The formatted enum key.
	 */
	formatEnumKey(enumKey: string, capitalizeFirstLetter: boolean = false) {
		let spacesInserted = util.formatCamelCase(enumKey);

		if (capitalizeFirstLetter) {
			spacesInserted = spacesInserted[0].toUpperCase() + (spacesInserted.length > 1 ? spacesInserted.substring(1) : '');
		}

		return spacesInserted.replace('_', ' - ');
	},

	/**
	 * Checks if an object is empty.
	 * @param {unknown} obj - The object to check.
	 * @returns {boolean} Whether the object is empty.
	 */
	isEmptyObject(obj: unknown) {
		if (obj == null || typeof obj !== 'object') return false;

		const prototype = Object.getPrototypeOf(obj);
		if (prototype !== null && prototype !== Object.prototype) return false;

		for (const objProp in obj) {
			if (Object.hasOwn(obj, objProp)) return false;
		}

		return true;
	},

	/**
	 * Converts a character enum to a number.
	 * @param {string} charCode - The character code to convert.
	 * @returns {number | null} The converted number.
	 */
	convertCharEnumToNumber(charCode: string) {
		if (!charCode) return null;
		return charCode.toString().charCodeAt(0);
	},

	/**
	 * Logs a message.
	 * @param {string} string - The message to log.
	 */
	log(string: string) {
		if (settings.environmentName === 'DEVELOPMENT') console.log(string);
		else LogRocket.log(string);
	},

	/**
	 * Logs an error message.
	 * @param {string} string - The message to log.
	 */
	error(string: string) {
		if (settings.environmentName === 'DEVELOPMENT') console.error(string);
		else LogRocket.error(string);
	},

	/**
	 * Gets the key of an enum value.
	 * @param {Record<string, T>} enumObj - The enum object.
	 * @param {T} value - The value to get the key of.
	 * @returns {string} The key of the value.
	 */
	getEnumKey<T>(enumObj: T, value: any) {
		return Object.keys(enumObj).find(key => enumObj[key as keyof T] === value);
	},
};

/**************************************************** */
/********* Made outside of the util object ********** */
/********** so you can use it in any file *********** */
/********* without importing the util object ******** */
/**************************************************** */

/**
 * Copies text to the clipboard.
 * Made outside of the util object so you can use it in any file without importing the util object.
 * @param {string} text - The text to copy.
 */
export const copyToClipboard = (text: string) => {
	navigator.clipboard.writeText(text);
	util.toastr('success', 'Copied to clipboard', text);
};

export default util;

//**
// Checks if two values are within a tolerance of each other.
// @param {number} value1 - The first value.
// @param {number} value2 - The second value.
// @param {number} tolerance - The tolerance.
// @returns {boolean} Whether the values are within the tolerance.
// */
export function AreValuesWithinTolerance(value1: number, value2: number, tolerance: number) {
	return Math.abs(value1 - value2) <= tolerance;
}

//**
// Checks if a value is within a tolerance of another value.
// @param {number} value - The value to check.
// @param {number} value2 - The value to check against.
// @param {number} tolerancePercentage - The tolerance percentage.
// @returns {boolean} Whether the value is within the tolerance.
// */
export function AreValuesWithinTolerancePorcentage(value: number, value2: number, tolerancePercentage: number) {
	const tolerance = value * tolerancePercentage;
	return AreValuesWithinTolerance(value, value2, tolerance);
}
